/**
 * Canvas Engine SOT — Color Utilities
 *
 * Unified color helpers extracted from editor.js and render-worker.ts.
 */

/**
 * Normalize a hex color string. Ensures it starts with '#'.
 */
export function graphHexColor(input: string | null | undefined, fallback = '#2563eb'): string {
  if (!input || typeof input !== 'string') return fallback;
  if (input.startsWith('#')) return input;
  return `#${input}`;
}

/**
 * Convert hex color + alpha (0-1) to an `rgba(...)` string.
 */
export function graphApplyAlpha(hex: string, alpha: number): string {
  const normalized = graphHexColor(hex, '#0F172A').replace('#', '');
  const clampedAlpha = Math.max(0, Math.min(1, Number.isFinite(alpha) ? alpha : 1));
  const r = parseInt(normalized.slice(0, 2) || '00', 16);
  const g = parseInt(normalized.slice(2, 4) || '00', 16);
  const b = parseInt(normalized.slice(4, 6) || '00', 16);
  return `rgba(${Number.isFinite(r) ? r : 0},${Number.isFinite(g) ? g : 0},${Number.isFinite(b) ? b : 0},${clampedAlpha})`;
}

/**
 * Linearly interpolate between two hex colors.
 * strength=0 returns base, strength=1 returns target.
 */
export function graphMixColor(baseHex: string, targetHex: string, strength: number): string {
  const base = graphHexColor(baseHex, '#2563eb').replace('#', '');
  const target = graphHexColor(targetHex, '#ffffff').replace('#', '');
  const mix = Math.max(0, Math.min(1, Number(strength) || 0));

  const br = parseInt(base.slice(0, 2) || '00', 16) || 0;
  const bg = parseInt(base.slice(2, 4) || '00', 16) || 0;
  const bb = parseInt(base.slice(4, 6) || '00', 16) || 0;
  const tr = parseInt(target.slice(0, 2) || '00', 16) || 0;
  const tg = parseInt(target.slice(2, 4) || '00', 16) || 0;
  const tb = parseInt(target.slice(4, 6) || '00', 16) || 0;

  const lr = Math.round(br * (1 - mix) + tr * mix);
  const lg = Math.round(bg * (1 - mix) + tg * mix);
  const lb = Math.round(bb * (1 - mix) + tb * mix);

  return `#${[lr, lg, lb]
    .map((channel) => channel.toString(16).padStart(2, '0'))
    .join('')
    .toUpperCase()}`;
}

/**
 * Resolve a color value to a valid CSS color string.
 * Handles bare hex (abc123), #-prefixed (#abc123), and rgb/rgba strings.
 * Superset of editor's resolveColor + worker's resolveColor.
 */
export function resolveColor(value: string | null | undefined, fallback: string): string {
  if (value == null) return fallback;
  const str = String(value).trim();
  if (!str) return fallback;
  if (/^#/.test(str)) return str;
  if (/^[0-9a-f]{3,8}$/i.test(str)) return '#' + str;
  return str;
}

/**
 * Apply an alpha multiplier to any color (hex or rgba).
 * Port of render-worker's `withAlpha` — more robust than graphApplyAlpha:
 * handles 3/4/6/8-digit hex and existing rgba() strings.
 */
export function withAlpha(color: string, alpha: number): string {
  const base = resolveColor(color, '#000000');
  const rawAlpha = Number(alpha);
  const normalizedAlpha = Math.max(0, Math.min(1, Number.isFinite(rawAlpha) ? rawAlpha : 0));

  if (/^rgba?\(/i.test(base)) {
    const match = base.match(/rgba?\(([^)]+)\)/i);
    if (match) {
      const parts = match[1].split(',').map((part) => Number(part.trim()));
      const r = Number.isFinite(parts[0]) ? parts[0] : 0;
      const g = Number.isFinite(parts[1]) ? parts[1] : 0;
      const b = Number.isFinite(parts[2]) ? parts[2] : 0;
      const existingAlpha = Number.isFinite(parts[3]) ? parts[3] : 1;
      const finalAlpha = Math.max(0, Math.min(1, existingAlpha * normalizedAlpha));
      return `rgba(${r}, ${g}, ${b}, ${finalAlpha})`;
    }
  }

  const hex = base.replace('#', '');
  let r = 0, g = 0, b = 0, existingAlpha = 1;

  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 4) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
    existingAlpha = parseInt(hex[3] + hex[3], 16) / 255;
  } else if (hex.length === 6 || hex.length === 8) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
    if (hex.length === 8) {
      existingAlpha = parseInt(hex.substring(6, 8), 16) / 255;
    }
  } else {
    return `rgba(0, 0, 0, ${normalizedAlpha})`;
  }

  const finalAlpha = Math.max(0, Math.min(1, existingAlpha * normalizedAlpha));
  return `rgba(${r}, ${g}, ${b}, ${finalAlpha})`;
}
